'use client'

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { Contract } from '@/lib/db/schema/contracts'
import type { Serialized } from '@/lib/db/mappers'
import { fetchApi } from '@/lib/utils/fetch-api'
import { parseQueryErrorMessage } from '@/lib/api/parse-query-error'

type ApiContract = Serialized<Contract>
/** DW-7 C-2: PATCH成功時、副作用(顧客ステータス自動更新等)の失敗を伝える任意フィールド */
type ApiContractWithWarning = ApiContract & { _warning?: string }

// ============================================
// Filter Types
// ============================================
export interface ContractFilterOptions {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  status?: string
  customerId?: string
}

export interface PaginatedResult<T> {
  data: T[]
  total: number
  page: number
  limit: number
  totalPages: number
  hasMore: boolean
}

// ============================================
// Query Keys
// ============================================
export const contractKeys = {
  all: ['contracts'] as const,
  lists: () => [...contractKeys.all, 'list'] as const,
  list: (filters: ContractFilterOptions) => [...contractKeys.lists(), filters] as const,
  details: () => [...contractKeys.all, 'detail'] as const,
  detail: (id: string) => [...contractKeys.details(), id] as const,
  byCustomer: (customerId: string) => [...contractKeys.all, 'customer', customerId] as const,
}

// ============================================
// Helper: Build query string from filters
// ============================================
function buildContractParams(filters: ContractFilterOptions): string {
  const params = new URLSearchParams()
  if (filters.page != null) params.set('page', String(filters.page))
  if (filters.limit != null) params.set('limit', String(filters.limit))
  if (filters.sortBy) params.set('sortBy', filters.sortBy)
  if (filters.sortOrder) params.set('sortOrder', filters.sortOrder)
  if (filters.status) params.set('status', filters.status)
  if (filters.customerId) params.set('customerId', filters.customerId)
  return params.toString()
}

// ============================================
// Hooks
// ============================================

/**
 * ページネーション付き契約一覧取得
 */
export function useContracts(filters: ContractFilterOptions = {}) {
  return useQuery({
    queryKey: contractKeys.list(filters),
    queryFn: async () => {
      const qs = buildContractParams(filters)
      const res = await fetch(`/api/contracts?${qs}`, {
        headers: { 'Content-Type': 'application/json' },
      })
      if (!res.ok) {
        throw new Error(await parseQueryErrorMessage(res))
      }
      const json = await res.json()
      // Handle both paginated and non-paginated responses
      if (json.pagination) {
        return {
          data: json.data,
          total: json.pagination.total ?? 0,
          page: json.pagination.page ?? 1,
          limit: json.pagination.limit ?? 50,
          totalPages: json.pagination.totalPages ?? 1,
          hasMore: json.pagination.hasMore ?? false,
        } as PaginatedResult<ApiContract>
      }
      // Non-paginated fallback: data array directly
      const data = json.data ?? json
      // 200 でも配列でない応答は契約崩れ=障害。[] に潰すと画面が「0件」に化けて
      // 失敗を隠す(偽0・use-g-plans PR #8941 と同型)。error に倒して isError に載せる。
      if (!Array.isArray(data)) {
        throw new Error('契約一覧の応答形式が不正です（配列ではありません）')
      }
      return {
        data,
        total: data.length,
        page: 1,
        limit: data.length,
        totalPages: 1,
        hasMore: false,
      } as PaginatedResult<ApiContract>
    },
    placeholderData: (previousData) => previousData,
    staleTime: 2 * 60 * 1000,
    gcTime: 2 * 60 * 1000,
  })
}

/**
 * 顧客別契約取得
 */
export function useContractsByCustomer(customerId: string) {
  return useQuery({
    queryKey: contractKeys.byCustomer(customerId),
    queryFn: () =>
      fetchApi<ApiContract[]>(`/api/contracts?paginated=false&customerId=${customerId}`),
    enabled: !!customerId,
    staleTime: 2 * 60 * 1000,
    gcTime: 2 * 60 * 1000,
  })
}

/**
 * 単一契約取得
 */
export function useContract(id: string) {
  return useQuery({
    queryKey: contractKeys.detail(id),
    queryFn: () => fetchApi<ApiContract>(`/api/contracts/${id}`),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
    gcTime: 2 * 60 * 1000,
  })
}

/**
 * 契約作成
 *
 * 従業員SSOT ACT-7: contracts の氏名 text 写し列(*_person)は DROP 対象で DB には保存されない
 * （書込 choke-point omitContractStaffText が insert 前に落とす）。ただし API の
 * createContractSchema は業務要件「営業担当なしの契約は作れない」を FK か氏名のどちらかで
 * 担保するため、氏名しか持たない経路（契約ウィザード）が `salesPerson` を **transient 入力**
 * として送れるよう型に残す（保存はされない）。
 */
type CreateContractInput = Partial<ApiContract> & { salesPerson?: string | null }

export function useCreateContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (contract: CreateContractInput) =>
      fetchApi<ApiContract>('/api/contracts', {
        method: 'POST',
        body: JSON.stringify(contract),
      }),
    onSuccess: (newContract) => {
      queryClient.setQueryData(contractKeys.detail(newContract.id), newContract)
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
      if (newContract.customerId) {
        queryClient.invalidateQueries({
          queryKey: contractKeys.byCustomer(newContract.customerId),
        })
      }
    },
  })
}

/**
 * 契約更新
 */
export function useUpdateContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      updates,
      expectedUpdatedAt,
    }: {
      id: string
      updates: Partial<ApiContract>
      /**
       * 楽観ロック token。明示指定が無ければ React Query キャッシュ
       * (contractKeys.detail) の updatedAt を自動添付する。
       */
      expectedUpdatedAt?: string
    }) => {
      const cached = queryClient.getQueryData<ApiContract>(contractKeys.detail(id))
      let token = expectedUpdatedAt ?? cached?.updatedAt
      // キャッシュ未初期化・PWA キャッシュ等で token が取れない場合は GET で取り直す。
      // ここで諦めると「保存に失敗しました」の原因不明エラーになる（顧客側 #5813 と同型）。
      if (!token) {
        const fresh = await fetchApi<ApiContract>(`/api/contracts/${id}`)
        token = fresh.updatedAt ?? undefined
      }
      if (!token) {
        throw new Error(
          '楽観ロック token (expectedUpdatedAt) が不足しています。GET で契約を取得してから更新してください'
        )
      }
      // 🔴 409（他者が先に保存済み）を受けても自動再送しない。顧客側 owner 判断 A と同じ形。
      // 判断材料: docs/運用/同時編集-lost-update-owner判断-2026-08-12.md
      return fetchApi<ApiContractWithWarning>(`/api/contracts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ ...updates, expectedUpdatedAt: token }),
      })
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: contractKeys.detail(id) })

      const previousContract = queryClient.getQueryData<ApiContract>(contractKeys.detail(id))

      if (previousContract) {
        // 🔴 updatedAt は触らない。フロント時刻を書き込むと、次の mutationFn が
        // それを楽観ロック token として拾い、サーバ側の実 updated_at と必ずズレて
        // 常時 409 になる（顧客側 #5821 と同型の構造的バグ）。
        // 正しい値は onSettled の再取得でサーバから来る。
        // `...updates` の後に明示的に戻すのは、呼び出し側が updates に updatedAt を
        // 混ぜても token が汚染されないようにするため（Codex SO 再レビュー指摘）。
        queryClient.setQueryData(contractKeys.detail(id), {
          ...previousContract,
          ...updates,
          updatedAt: previousContract.updatedAt,
        })
      }

      return { previousContract }
    },
    // 🔴 保存成功のたびにサーバ応答をキャッシュへ反映する。これが無いと、
    // 続けて保存したとき 2 回目が旧 updatedAt を token として送り、
    // 「自分の直前の保存」に対して 409 になる（onSettled の再取得は待たれない）。
    // 409 の自動再送を廃止した（owner 判断 A）ため、これは画面にエラーとして見える。
    // 🔴 置き換えず **merge** する。GET /api/contracts/[id] は resolveContractStaffDisplay を
    // 通して salesPersonDisplay 等の表示用フィールドを付与するが、PATCH は raw な contracts 行
    // しか返さない。丸ごと置換すると、再取得が終わるまで担当者表示が欠落する（Codex SO 4巡目 P2）。
    onSuccess: (updated, { id }) => {
      // DW-7 C-2: 顧客ステータス自動更新等の副作用失敗は _warning で伝わってくるが、
      // これまでどのUIも読んでおらず死んだ機構だった。契約更新自体は成功しているため
      // toast.error ではなく toast.warning で「一部失敗」であることを示す。
      if (updated?._warning) toast.warning(updated._warning)
      if (!updated?.updatedAt) return
      queryClient.setQueryData(contractKeys.detail(id), (prev: ApiContract | undefined) =>
        prev ? { ...prev, ...updated } : updated
      )
    },
    onError: (err, variables, context) => {
      if (context?.previousContract) {
        queryClient.setQueryData(contractKeys.detail(variables.id), context.previousContract)
      }
    },
    onSettled: (_, __, { id }) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
    },
  })
}

/**
 * 契約削除
 */
export function useDeleteContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => fetchApi<void>(`/api/contracts/${id}`, { method: 'DELETE' }),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: contractKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
    },
  })
}

/**
 * 承認申請
 */
export function useSubmitForApproval() {
  const queryClient = useQueryClient()

  return useMutation({
    // #10591 項目15: 実行者はサーバがセッションから決める。client は識別子を送らない。
    mutationFn: ({ id }: { id: string }) =>
      fetchApi<ApiContract>(`/api/contracts/${id}/submit`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: (updatedContract) => {
      queryClient.setQueryData(contractKeys.detail(updatedContract.id), updatedContract)
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
    },
  })
}

/**
 * 契約承認
 */
export function useApproveContract() {
  const queryClient = useQueryClient()

  return useMutation({
    // #10591 項目15: 承認者はサーバがセッションから決める。client は識別子を送らない。
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      fetchApi<ApiContract>(`/api/contracts/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ comment }),
      }),
    onSuccess: (updatedContract) => {
      queryClient.setQueryData(contractKeys.detail(updatedContract.id), updatedContract)
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
    },
  })
}

/**
 * 契約差戻し
 */
export function useReturnContract() {
  const queryClient = useQueryClient()

  return useMutation({
    // #10591 項目15: 差戻し実行者はサーバがセッションから決める。client は識別子を送らない。
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      fetchApi<ApiContract>(`/api/contracts/${id}/return`, {
        method: 'POST',
        body: JSON.stringify({ comment }),
      }),
    onSuccess: (updatedContract) => {
      queryClient.setQueryData(contractKeys.detail(updatedContract.id), updatedContract)
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
    },
  })
}
